import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { useFirestore } from 'vuefire'
import { collection, getDocs, query, where } from 'firebase/firestore'
import type { Firestore } from 'firebase/firestore'
import { parseRegistryFamilyProjection, type RegistryFamilyProjection } from '@/models/registry'
import { logger } from '@/utils/logger'
import {
  liveFamilyId,
  sameFamily,
  identitySet,
  canonicalKey,
  sameEdition,
  type EditionRef,
  type RegistryLookup,
} from '@/utils/registryReferences'

/**
 * MYAC-904 P4.2 — batched, session-cached `registry_families` read layer.
 *
 * The front resolves regulation_lists rows LIVE against `registry_families`
 * (spec `work/2026-07-27-registry-identity-simplification/spec.md` §8/§9),
 * never a stored resolution stamp. Rules (deployed 2026-07-29) grant read on
 * `registry_families/{id}` only when `exposable == true`; the sanctioned
 * query shape is `where('identifiers', 'array-contains-any', chunk)` in
 * chunks of <=30 keys (Firestore's hard limit on that operator, spec F21).
 *
 * A family "matches" a row when the row's `registry_ref.family_id` is a
 * member of `family.identifiers` — array-contains-any returns the UNION of
 * docs matching ANY key in the chunk, so each returned family must be
 * re-checked locally against every key it could plausibly match (a family
 * hit on one row's key doesn't imply it also carries another row's key).
 *
 * Cache lifecycle: keyed by the identifier string as carried by a row's
 * `registry_ref.family_id` (never by Firestore doc-id — a family's doc-id is
 * just one of its identifiers, spec §2). Lives for the store's lifetime
 * (Pinia singleton = one browser session). `resolveFamilies` only queries
 * for keys not already cached; `resetCache()` forces a full re-resolve.
 *
 * MYAC-1002 workstream B — the norms view used to call `resetCache()`
 * UNCONDITIONALLY on every mount ("re-resolve on page change", spec §8
 * batching note), which cost ~11 chunked `registry_families` queries plus a
 * full 312-row display rebuild every single time the user navigated back to
 * the list. That reset only ever existed to bound STALENESS — `registry_families`
 * is a global, curator-owned collection that this app never writes, so
 * nothing the user does here can invalidate it; only curation elsewhere can,
 * and only over time. So the reset is now conditional (`refreshIfStale`):
 *   - the cache is dropped when the SCOPE changed (a different project, hence
 *     possibly a different company — never serve one scope's reads to another), or
 *   - when the current cache generation is older than `FAMILY_CACHE_TTL_MS`.
 * A full page reload always starts from an empty cache (new Pinia instance),
 * so this only ever affects same-session, back-and-forth navigation.
 *
 * Fail-closed, never throws into the caller: a chunk that errors (most
 * commonly permission-denied because rules aren't deployed on some env)
 * resolves every one of its keys to `null` ("not found") rather than
 * rejecting — the table must never crash on a registry read failure.
 *
 * MYAC-904 perf fix (norms table sluggish at 312 rows — see
 * `RegulatoryMonitoringNormsView.vue`'s `displayRows` doc comment): `_cache`
 * is `markRaw`'d so Pinia/Vue never wraps it in a deep-reactive Map proxy.
 * Before this fix, every consumer that merely CALLED `familyFor(key)` inside
 * a computed (even just to read one key) registered a fine-grained reactive
 * dependency on that Map — and because `_resolveChunk` used to reassign
 * `_cache` once per chunk, that reassignment alone invalidated every such
 * dependent computed, regardless of which keys the chunk actually touched.
 * With N chunks in flight that meant an O(rows) recompute N times over —
 * observed live: 312 rows × 11 chunks == 3744 rebuilds of the same 312 rows
 * for a single page load. (MYAC-1002 loop fix, 2026-08-12: chunks now write
 * IN PLACE on the shared markRaw Map — the per-chunk copy-and-swap also had
 * a lost-update race between parallel chunks that kept the cache from ever
 * converging on large projects, re-firing the resolve cycle forever.) `_cache` being non-reactive means reading it registers NO
 * dependency at all; `cacheVersion` (a plain reactive number) is the
 * ONLY thing consumers should depend on to know "something resolved" —
 * cheap to track, and callers can pair it with a per-row cache (comparing
 * `familyFor(key)`'s return value by reference) to only rebuild the rows
 * whose own family actually changed instead of the whole set.
 */

/** Firestore's `array-contains-any` accepts at most 30 comparison values. */
export const FAMILY_QUERY_CHUNK_SIZE = 30

/**
 * MYAC-1002 workstream B — how long one cache generation may be reused before
 * a (re-)mount re-resolves everything. Bounds how long curation done
 * elsewhere (web-admin) can stay invisible to a user who never reloads the
 * page; 5 minutes keeps rapid back-and-forth navigation free while staying
 * well inside "the curator told me to look again" latency.
 */
export const FAMILY_CACHE_TTL_MS = 5 * 60 * 1000

export function chunkKeys(keys: string[], size: number = FAMILY_QUERY_CHUNK_SIZE): string[][] {
  const out: string[][] = []
  for (let i = 0; i < keys.length; i += size) out.push(keys.slice(i, i + size))
  return out
}

export const useRegistryFamiliesStore = defineStore('registryFamilies', {
  state: () => ({
    // undefined = never queried; null = queried, confirmed not found (or the
    // query errored — fail-closed treats the two identically on purpose).
    // markRaw: never deep-reactive (see module doc comment) — reads never
    // register a dependency; `cacheVersion` below is the reactive signal.
    _cache: markRaw(new Map<string, RegistryFamilyProjection | null>()),
    /** Key -> in-flight chunk promise. Overlapping `resolveFamilies`
     *  callers (mount + the `regulations` watch + the `cacheVersion`
     *  successor watch) never re-query a key already on the wire — they
     *  AWAIT the existing fetch instead, preserving the call sites'
     *  "await resolveFamilies ⇒ familyFor(key) is settled" contract
     *  (review finding: a bare key-set dedupe returned early and broke
     *  `ensureSuccessorFamilyResolved`'s assumption). markRaw for the
     *  same reason as `_cache`: bookkeeping, never a reactive signal. */
    _pending: markRaw(new Map<string, Promise<void>>()),
    /** Bumped by `resetCache()`. An in-flight chunk that started under an
     *  older generation discards its result instead of writing stale data
     *  into the fresh cache (chunk completes AFTER a reset). Plain number,
     *  never a reactive signal. */
    _generation: 0,
    /** Bumped once per resolved chunk (success or fail-closed) and once on
     *  `resetCache()`. The ONLY reactive signal this store exposes for "the
     *  cache changed" — consumers that need to react to new resolutions
     *  should depend on this plain number, not on `_cache` itself. */
    cacheVersion: 0,
    isResolving: false,
    /** MYAC-1002 workstream B — the scope this cache generation was filled
     *  for (the norms view passes the current project id). A different scope
     *  means a different project and possibly a different company: drop the
     *  cache rather than serve one scope's reads to another. */
    scopeKey: '' as string,
    /** MYAC-1002 workstream B — epoch-ms when the current cache generation
     *  started (`0` = never established). Drives the TTL in `refreshIfStale`. */
    generationStartedAt: 0,
  }),
  getters: {
    /** Cached lookup by the identifier a row's `registry_ref.family_id`
     *  carries. `undefined` = not yet resolved (call `resolveFamilies`
     *  first); `null` = resolved, no exposable family found. */
    familyFor: (state) => {
      return (key: string | null | undefined): RegistryFamilyProjection | null | undefined => {
        if (!key) return null
        return state._cache.get(key)
      }
    },
    /** MYAC-1630 — the resolved cache as a `RegistryLookup` for the pure
     *  `@/utils/registryReferences` functions (null for a key not resolved
     *  yet). The bound getters below are those functions over this cache:
     *  a stored family id or revision key is compared through them, never as
     *  a string. Callers resolve the keys first, as for `familyFor`. */
    lookup: (state): RegistryLookup => {
      return (key: string) => (key ? (state._cache.get(key) ?? null) : null)
    },
    liveFamilyId(): (familyId: string | null | undefined) => string | null {
      const lookup = this.lookup
      return (familyId) => liveFamilyId(lookup, familyId)
    },
    sameFamily(): (a: string | null | undefined, b: string | null | undefined) => boolean {
      const lookup = this.lookup
      return (a, b) => sameFamily(lookup, a, b)
    },
    identitySet(): (familyId: string | null | undefined) => ReadonlySet<string> {
      const lookup = this.lookup
      return (familyId) => identitySet(lookup, familyId)
    },
    canonicalKey(): (familyId: string | null | undefined, key: string | null | undefined) => string | null {
      const lookup = this.lookup
      return (familyId, key) => canonicalKey(lookup, familyId, key)
    },
    sameEdition(): (a: EditionRef, b: EditionRef) => boolean {
      const lookup = this.lookup
      return (a, b) => sameEdition(lookup, a, b)
    },
  },
  actions: {
    /** Drop the whole session cache, forcing the next `resolveFamilies` call
     *  to re-query everything, and start a new cache generation. Callers that
     *  just want "fresh enough" should use `refreshIfStale` instead. */
    resetCache() {
      this._cache = markRaw(new Map())
      // A new generation owes nothing to in-flight fetches of the old one —
      // clearing lets the next resolveFamilies re-request those keys instead
      // of assuming a doomed fetch will still deliver them.
      this._pending = markRaw(new Map())
      this._generation++
      this.cacheVersion++
      this.generationStartedAt = Date.now()
    },

    /**
     * MYAC-1002 workstream B — the cache-retaining replacement for a blind
     * `resetCache()` on every view mount. Resets ONLY when the cache cannot
     * legitimately be reused:
     *   - `scopeKey` changed (different project → possibly different company), or
     *   - the current generation is older than `maxAgeMs` (curation elsewhere
     *     must eventually become visible without a page reload), or
     *   - no generation was ever established (`generationStartedAt === 0`),
     *     e.g. the cache was warmed by a report card before the list was ever
     *     opened — that partial fill must not pass for a full one.
     *
     * Returns true when it actually reset, so callers can re-resolve.
     */
    refreshIfStale(scopeKey: string, maxAgeMs: number = FAMILY_CACHE_TTL_MS): boolean {
      const expired =
        this.generationStartedAt === 0 || Date.now() - this.generationStartedAt > maxAgeMs
      if (scopeKey === this.scopeKey && !expired) return false
      this.resetCache()
      this.scopeKey = scopeKey
      return true
    },

    /**
     * Resolve every given identifier (typically the visible rows'
     * `registry_ref.family_id`), skipping any already cached. Batches the
     * still-missing keys into chunks of <=30 and fires them in parallel.
     * Never throws — failures degrade individual keys to `null`.
     */
    async resolveFamilies(familyIdKeys: Array<string | null | undefined>): Promise<void> {
      const unique = Array.from(new Set(familyIdKeys.filter((k): k is string => !!k)))
      const toAwait: Promise<void>[] = []
      const missing: string[] = []
      for (const k of unique) {
        if (this._cache.has(k)) continue
        const inflight = this._pending.get(k)
        if (inflight) toAwait.push(inflight)
        else missing.push(k)
      }
      if (missing.length === 0 && toAwait.length === 0) return

      // Capture the Map INSTANCE we register on: `resetCache()` swaps
      // `_pending` for a fresh one, and a stale fetch's cleanup must not
      // evict entries a post-reset caller legitimately re-registered (the
      // identity check below makes the cleanup a strict no-op then).
      const pendingMap = this._pending
      if (missing.length > 0) {
        this.isResolving = true
        const firestore = useFirestore()
        for (const c of chunkKeys(missing)) {
          const wrapped: Promise<void> = this._resolveChunk(firestore, c).finally(() => {
            for (const k of c) if (pendingMap.get(k) === wrapped) pendingMap.delete(k)
          })
          for (const k of c) pendingMap.set(k, wrapped)
          toAwait.push(wrapped)
        }
      }
      try {
        await Promise.all(toAwait)
      } finally {
        // true while ANY current-generation fetch is still on the wire
        this.isResolving = this._pending.size > 0
      }
    },

    async _resolveChunk(firestore: Firestore, keys: string[]): Promise<void> {
      const generation = this._generation
      try {
        const q = query(
          collection(firestore, 'registry_families'),
          // The exposable filter is NOT optional: the Firestore rule is
          // `allow read: if ... resource.data.exposable == true`, and for a
          // LIST query the rules engine must prove every possible result
          // satisfies it FROM THE QUERY SHAPE — without this clause the
          // whole query is permission-denied (observed live 2026-07-29:
          // every chip fail-closed). Served by the deployed composite index
          // (exposable ASC, identifiers CONTAINS) — spec §9/F21.
          where('exposable', '==', true),
          where('identifiers', 'array-contains-any', keys),
        )
        const snap = await getDocs(q)
        if (generation !== this._generation) return // reset raced this fetch — result is stale
        const families = snap.docs
          .map((d) => parseRegistryFamilyProjection(d.id, d.data()))
          .filter((f): f is RegistryFamilyProjection => f !== null)

        // MYAC-904 UX fix #5 + MYAC-1002 loop fix (2026-08-12): one reactive
        // write per CHUNK, not one per key — `this._cache` is markRaw'd, so
        // per-key `.set()` never touches Vue reactivity; `cacheVersion` is
        // the single per-chunk signal. The sets are IN PLACE on the shared
        // Map, NEVER a copy-and-swap: chunks resolve in PARALLEL, and each
        // `new Map(this._cache)` snapshot silently dropped every key a
        // concurrent chunk had landed in between (lost-update race). At 11
        // chunks the clobbering mostly converged; at ~43 (1295-row project)
        // it never did — the cacheVersion watcher re-collected the lost keys,
        // re-queried, and the page sat in a permanent resolve loop burning
        // ~2s of main thread per cycle (profiled live, 2026-08-12). Per-key
        // in-place sets are safe for readers: a key is either absent or
        // carries its complete value — there is no half-populated state.
        for (const key of keys) {
          const match = families.find((f) => f.identifiers.includes(key)) ?? null
          this._cache.set(key, match)
        }
        this.cacheVersion++
      } catch (error) {
        // Rules not deployed on this env, transient network error, etc. —
        // fail closed (never crash the table): every key in this chunk is
        // treated as "no exposable family found", same as a genuine miss.
        if (generation !== this._generation) return // reset raced this fetch — nothing to log, result discarded
        logger.warn('registryFamilies: batched resolve failed, falling back to not-found for this chunk', error)
        for (const key of keys) {
          this._cache.set(key, null)
        }
        this.cacheVersion++
      }
    },
  },
})
